import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Loader2 } from "lucide-react";

const PROFESSIONS = [
  "Advocate",
  "Business Owner",
  "Builder / Developer",
  "Bank / Financial Institution",
  "Government / PSU",
  "Individual",
  "Other",
];

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", profession: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(form.name.trim(), form.email.trim(), form.password, form.profession || null);
      nav("/book", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-parchment">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-9 shadow-card">
          <div className="text-[10px] tracking-overline uppercase text-gold font-bold">{t("auth.get_started_overline")}</div>
          <h1 className="font-display text-4xl text-navy-deep mt-1 mb-2">{t("auth.register_title")}</h1>
          <p className="text-sm text-slate-500 mb-7">{t("auth.register_subtitle")}</p>
          {err && (
            <div className="mb-5 px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="register-error">{err}</div>
          )}
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.full_name")}</label>
              <input
                type="text" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                placeholder="e.g. Anjali Patil"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.profession")}</label>
              <select
                value={form.profession}
                onChange={(e) => setForm({ ...form, profession: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                data-testid="register-profession"
              >
                <option value="">{t("auth.profession_placeholder")}</option>
                {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.email")}</label>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                placeholder="you@example.com"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-overline uppercase font-bold text-slate-600 mb-1.5">{t("auth.password")}</label>
              <input
                type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 focus:border-navy focus:ring-1 focus:ring-navy outline-none rounded-sm bg-white"
                placeholder="At least 6 characters"
                data-testid="register-password"
              />
            </div>
            <button
              type="submit" disabled={busy}
              className="w-full bg-navy text-parchment py-3.5 rounded-sm hover:bg-gold transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit"
            >
              {busy && <Loader2 size={16} className="animate-spin"/>}
              {busy ? t("auth.creating") : t("auth.create_btn")}
            </button>
          </form>
          <p className="mt-7 text-sm text-slate-500 text-center">
            {t("auth.have_account")} <Link to="/login" className="text-gold hover:underline" data-testid="register-login-link">{t("auth.signin_link")}</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
